import useReadingStats from "api/UserResource/useReadingStats";
import useProfile from "api/UserResource/useProfile";
import AccountLayout from "../AccountLayout";
import { Container } from "layouts";
import PaddingX from "layouts/PaddingX";
import { HiBookOpen, HiClock, HiShoppingBag, HiTag } from "react-icons/hi";

const formatReadingTime = (minutes) => {
	if (!minutes || minutes === 0) return "0m";
	const hours = Math.floor(minutes / 60);
	const mins = minutes % 60;
	if (hours > 0) {
		return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
	}
	return `${mins}m`;
};

const ReadingStats = () => {
	const { data: profile, isLoading: isLoadingProfile } = useProfile();
	const { data: stats, isLoading: isLoadingStats } = useReadingStats();

	const booksRead = stats?.books_read ?? 0;
	const readingTimeMinutes = stats?.reading_time_minutes ?? 0;
	const booksPurchased = stats?.books_purchased ?? 0;
	const genreBreakdown = stats?.genre_breakdown ?? [];

	const isEmpty = !isLoadingStats && booksRead === 0 && booksPurchased === 0;

	return (
		<Container>
			<div className="min-h-screen bg-gray-50">
				<div className="pt-8 pb-16">
					<PaddingX>
						<AccountLayout isLoading={isLoadingProfile} profile={profile?.data}>
							<div className="mb-10">
								<h1 className="text-2xl font-semibold text-gray-900">Reading Statistics</h1>
								<p className="mt-1 text-sm text-gray-500">
									Your reading activity and purchase history at a glance.
								</p>
							</div>

							{isLoadingStats ? (
								<div className="flex justify-center py-16">
									<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-akgreen" />
								</div>
							) : isEmpty ? (
								<div className="flex flex-col items-center justify-center py-16 px-4 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
									<HiBookOpen className="w-16 h-16 text-gray-300 mb-4" />
									<h2 className="text-lg font-medium text-gray-700 mb-2">No reading data yet</h2>
									<p className="text-gray-500 text-center max-w-sm">
										Start reading books and making purchases to see your statistics here.
									</p>
								</div>
							) : (
								<div className="space-y-8">
									<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
										<div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
											<div className="flex items-center gap-3">
												<div className="p-3 bg-akgreen/10 rounded-lg">
													<HiBookOpen className="w-8 h-8 text-akgreen" />
												</div>
												<div>
													<p className="text-2xl font-bold text-gray-900">{booksRead}</p>
													<p className="text-sm text-gray-500">Books read</p>
												</div>
											</div>
										</div>
										<div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
											<div className="flex items-center gap-3">
												<div className="p-3 bg-blue-100 rounded-lg">
													<HiClock className="w-8 h-8 text-blue-600" />
												</div>
												<div>
													<p className="text-2xl font-bold text-gray-900">
														{formatReadingTime(readingTimeMinutes)}
													</p>
													<p className="text-sm text-gray-500">Reading time</p>
												</div>
											</div>
										</div>
										<div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
											<div className="flex items-center gap-3">
												<div className="p-3 bg-amber-100 rounded-lg">
													<HiShoppingBag className="w-8 h-8 text-amber-600" />
												</div>
												<div>
													<p className="text-2xl font-bold text-gray-900">{booksPurchased}</p>
													<p className="text-sm text-gray-500">Books purchased</p>
												</div>
											</div>
										</div>
									</div>

									{genreBreakdown.length > 0 && (
										<div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
											<h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
												<HiTag className="w-5 h-5 text-akgreen" />
												Genre breakdown
											</h2>
											<ul className="space-y-2">
												{genreBreakdown.map((item, index) => (
													<li
														key={index}
														className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0"
													>
														<span className="text-gray-700">{item.genre}</span>
														<span className="font-medium text-akgreen">{item.count} books</span>
													</li>
												))}
											</ul>
										</div>
									)}
								</div>
							)}
						</AccountLayout>
					</PaddingX>
				</div>
			</div>
		</Container>
	);
};

export default ReadingStats;
